import { Minus, Plus } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import MarketFlag from "@/components/layout/MarketFlag";
import { Button } from "@/components/ui/Button";
import { formatMoneyShort } from "@/lib/money";
import type { Market } from "@/lib/useMarket";
import { cn } from "@/lib/utils";
import type { StockOverview } from "../lib/stockHealth";
import { BADGE_VARIANT } from "./StockStatus";

interface Props {
  label: string;
  productCount: number;
  summary: StockOverview;
  currency: string;
  // Users without the gross-margin permission get no value figures at all.
  valueVisible: boolean;
  open: boolean;
  onToggle: () => void;
  // Set on a market's band: shows the market's flag and a bold name, so it
  // stands apart from the product group bands below it.
  market?: Market;
}

// A product group's band on Inventory Overview: clicking anywhere on it
// shows or hides the group's products. The totals are the same as the cards
// above the table, for this group's products only.
export default function GroupHeader({
  label,
  productCount,
  summary,
  currency,
  valueVisible,
  open,
  onToggle,
  market,
}: Props) {
  const Icon = open ? Minus : Plus;
  const facts = [
    `${productCount.toLocaleString()} SKU`,
    `${summary.stock.toLocaleString()} units`,
    valueVisible && summary.value !== null && formatMoneyShort(summary.value, currency),
    summary.doc !== null && `${summary.doc.toLocaleString()} days cover`,
    summary.incoming > 0 && `${summary.incoming.toLocaleString()} incoming`,
  ].filter(Boolean);
  const chips = [
    { count: summary.critical, label: "stockout risk", variant: BADGE_VARIANT.critical },
    { count: summary.orderSoon, label: "order needed", variant: BADGE_VARIANT.order_soon },
    { count: summary.overstock, label: "excess", variant: BADGE_VARIANT.overstock },
    { count: summary.healthy, label: "healthy", variant: BADGE_VARIANT.healthy },
  ].filter((c) => c.count > 0);

  return (
    <div
      onClick={onToggle}
      className="cursor-pointer border-y border-border bg-muted"
    >
      <div
        className="flex w-max items-center gap-3 whitespace-nowrap py-1.5 pl-1 pr-3 text-sm"
        style={{ position: "sticky", left: 0 }}
      >
        <Button
          type="button"
          variant="ghost"
          aria-expanded={open}
          className={cn("h-8 gap-1.5 px-2", market && "font-semibold")}
        >
          <Icon className="size-3.5" aria-hidden />
          {market && <MarketFlag market={market} />}
          {label}
        </Button>
        <span className="tabular-nums">{facts.join(" · ")}</span>
        {chips.map((c) => (
          <Badge key={c.label} variant={c.variant}>
            {`${c.count.toLocaleString()} ${c.label}`}
          </Badge>
        ))}
      </div>
    </div>
  );
}
